/*eslint complexity: ["error", 52]*/
import React, { ReactNode } from 'react';
import * as Icon from 'react-bootstrap-icons';
import { Button, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { TFunction } from 'i18next';

import { GetPurchaseInvoiceExtendedDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { DownloadAttachment } from '../Attachments/DownloadAttachment';
import ClientInfo from '../Clients/ClientInfo';
import {
  PurchaseInvoiceStatusEnum,
  PurchaseInvoiceStatusObj,
} from '../../../common/pokCore/validation/enums';
import { TKeys } from '../../../translations';

function buildPurchaseInvoiceTitle(
  purchaseInvoiceId: string,
  t: TFunction<'fvzLng', 'fvzLng'>,
  tk: TKeys<'fvzLng'>,
  purchaseInvoice?: GetPurchaseInvoiceExtendedDto,
  tv?: boolean,
  authCheckFinance?: boolean,
  goToPurchaseInvoice?: (id: string) => void,
): ReactNode {
  const inActive = purchaseInvoice && !purchaseInvoice.active;

  return (
    <>
      {inActive && <b>{t(tk.finance.archive)}</b>}
      <span
        style={
          inActive
            ? {
                textDecoration: 'line-through',
                marginLeft: '8px',
              }
            : {}
        }
      >
        {purchaseInvoiceId === 'utworz' ? (
          t(tk.finance.newFvz)
        ) : (
          <>
            <p>
              {!purchaseInvoice?.originalInvoice?.id
                ? t(tk.finance.fvz)
                : t(tk.finance.correctingFvz)}{' '}
              <b>{purchaseInvoice?.number}</b>
              {purchaseInvoice?.originalInvoice?.id && (
                <>
                  {' '}
                  {t(tk.finance.correctedTo)}{' '}
                  <Button
                    variant="link"
                    className="p-0 align-baseline title-size"
                    onClick={() =>
                      goToPurchaseInvoice?.(
                        purchaseInvoice.originalInvoice!.id!,
                      )
                    }
                  >
                    {purchaseInvoice.originalInvoice.number}
                  </Button>
                </>
              )}
              {purchaseInvoice?.client?.name ? (
                <>
                  {' '}
                  {t(tk.finance.for)} <b>{purchaseInvoice.client.name}</b>
                  <ClientInfo
                    id={purchaseInvoice.client.id}
                    className="color-primary ms-2 mb-1"
                    iconSize={18}
                    placement="left"
                  />
                </>
              ) : null}
            </p>

            <p className="d-flex">
              {purchaseInvoice
                ? `${t(tk.common.sysNumber)} ${purchaseInvoice.systemNumber} `
                : null}
              {purchaseInvoice?.status ? (
                <>
                  [
                  <b>
                    {t(
                      PurchaseInvoiceStatusObj[purchaseInvoice?.status],
                    ).toUpperCase()}
                    {purchaseInvoice?.status ===
                      PurchaseInvoiceStatusEnum.Sent &&
                    purchaseInvoice?.packageFVZNumber
                      ? ` ${t(tk.finance.packageWithNumber)} ${purchaseInvoice?.packageFVZNumber}`
                      : ''}
                    {purchaseInvoice?.eodId ? `, EOD` : ''}
                    {authCheckFinance && purchaseInvoice?.paid
                      ? `, ${t(tk.invoice.paid).toUpperCase()}`
                      : null}
                  </b>
                  ]
                  {purchaseInvoice?.status !== PurchaseInvoiceStatusEnum.Sent &&
                  purchaseInvoice?.wasSent ? (
                    <>
                      &nbsp;
                      <span className="text-red">
                        {t(tk.finance.revertFromSent)}
                      </span>
                    </>
                  ) : null}
                </>
              ) : null}
              {tv && (
                <OverlayTrigger
                  overlay={<Tooltip>{t(tk.finance.tvInvoice)}</Tooltip>}
                >
                  <Icon.DisplayFill size={20} className="ms-2 icon-blue" />
                </OverlayTrigger>
              )}
              {purchaseInvoice?.attachment ? (
                <DownloadAttachment
                  id={purchaseInvoice.attachment.id}
                  icon={Icon.FilePdf}
                  tooltipText={t(tk.finance.openNewWindow)}
                />
              ) : null}
            </p>
            {purchaseInvoice?.correctionsNumbers?.length ? (
              <div className="d-flex align-items-center">
                <Icon.FileEarmarkMinusFill
                  className="icon-blue me-2 mt-2"
                  size={20}
                />
                <Card.Subtitle className="pt-3">
                  {purchaseInvoice.originalInvoice?.id
                    ? t(tk.invoice.otherCorrections)
                    : t(tk.invoice.withCorrectionsToOriginal)}
                  :{' '}
                  {purchaseInvoice.correctionsNumbers.map((num, idx) => {
                    const id = purchaseInvoice.correctionsIds?.[idx];
                    return (
                      <React.Fragment key={idx}>
                        {idx > 0 && ', '}
                        {id && goToPurchaseInvoice && (
                          <Button
                            variant="link"
                            className="p-0 align-baseline"
                            onClick={() => goToPurchaseInvoice(id)}
                          >
                            {num}
                          </Button>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Card.Subtitle>
              </div>
            ) : null}
            {inActive && purchaseInvoice?.deletingReason && (
              <div className="d-flex align-items-center">
                {t(tk.finance.reasonForDeleteFvz)}:{' '}
                <b className="ms-1">{purchaseInvoice?.deletingReason}</b>
              </div>
            )}
            {purchaseInvoice?.automaticApproved && (
              <div className="d-flex align-items-center">
                <span className="text-green">
                  {t(tk.finance.automaticApproved)}
                </span>
              </div>
            )}
          </>
        )}
      </span>
    </>
  );
}

export default buildPurchaseInvoiceTitle;
